/**
 * Report issue — category chips, visibility segmented, notify scope,
 * description, photo placeholder. Wired: POST /api/issues.
 * Loosely follows the SimpleCreate pattern (design/source has no dedicated
 * "new ticket" screen; ported from IssueCard/IssueDetail field semantics).
 */
import React, { useState } from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { Icon } from '@/components/gate/icons';
import { BottomBar, goBack, Segmented } from '@/features/community/bits';
import { PhotoAttach } from '@/components/photo/PhotoAttach';
import { createIssue, issueCatLabel, type IssueVisibility } from '@/features/community/lib';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Button, Chip, Field, IconButton, Input, Screen, TopBar } from '@/ui';

// Submit values stay English (the API expects them); only the labels localise.
const CATEGORIES = ['Plumbing', 'Electrical', 'Housekeeping', 'Security', 'Noise', 'Parking', 'Other'];

export default function ReportIssueScreen() {
  const { colors } = useTheme();
  const { t } = useT();

  const visibilityOptions = [
    { key: 'PERSONAL', label: t('ir_vis_personal') },
    { key: 'COMMUNITY', label: t('ir_vis_community') },
  ];
  const notifyOptions = [
    { key: 'MY_WING', label: t('ir_notify_wing') },
    { key: 'ALL', label: t('ir_notify_all') },
    { key: 'HELPDESK', label: t('ir_notify_helpdesk') },
  ];
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Plumbing');
  const [visibility, setVisibility] = useState<IssueVisibility>('PERSONAL');
  const [notifyScope, setNotifyScope] = useState('MY_WING');
  const [description, setDescription] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = title.trim().length > 0;

  const submit = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError(null);
    try {
      await createIssue({
        title: title.trim(),
        category,
        visibility,
        notifyScope: visibility === 'COMMUNITY' ? notifyScope : 'HELPDESK',
        description: description.trim(),
        photos: photo,
      });
      goBack();
    } catch {
      setError(t('ir_submit_error'));
      setSaving(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('ir_title')}
        onBack={goBack}
        right={
          <IconButton label={t('ir_discard')} onPress={goBack}>
            <Icon name="x" size={20} color={colors.textPrimary} />
          </IconButton>
        }
      />
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4 }}>
        <Field label={t('hd_field_title')}>
          <Input value={title} onChangeText={setTitle} placeholder={t('ir_title_ph')} />
        </Field>

        <Field label={t('hd_category')}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
            {CATEGORIES.map((c) => (
              <Chip key={c} label={issueCatLabel(c, t)} active={category === c} onPress={() => setCategory(c)} />
            ))}
          </View>
        </Field>

        <Field label={t('ir_visibility')} help={visibility === 'COMMUNITY' ? t('ir_vis_help_community') : t('ir_vis_help_personal')}>
          <Segmented options={visibilityOptions} value={visibility} onChange={(v) => setVisibility(v as IssueVisibility)} />
        </Field>

        {visibility === 'COMMUNITY' ? (
          <Field label={t('ir_notify')}>
            <Segmented options={notifyOptions} value={notifyScope} onChange={setNotifyScope} />
          </Field>
        ) : null}

        <Field label={t('hd_description')}>
          <Input
            value={description}
            onChangeText={setDescription}
            placeholder={t('ir_desc_ph')}
            multiline
            style={{ minHeight: 96, textAlignVertical: 'top' }}
          />
        </Field>

        <Field label={t('hd_photos')}>
          <PhotoAttach value={photo} onChange={setPhoto} />
        </Field>

        {error ? <AppText variant="bodySm" tone="danger">{error}</AppText> : null}
      </Screen>
      <BottomBar>
        <Button title={t('ir_submit')} size="lg" disabled={!canSave} loading={saving} onPress={() => void submit()} />
      </BottomBar>
    </Screen>
  );
}
